"use client";

import dynamic from "next/dynamic";

import useUpdateProgressBar from "@/app/common-hooks/progress/use-update-progress-bar";
import CancelEditModalContent from "@/app/shared-components/cancel-edit-modal";
import CookiePolicyButton from "@/app/shared-components/cookie-policy-button/cookie-policy-button";
import CountryChangeModal from "@/app/shared-components/country-change-modal/country-change-modal";
import QuestionFooterSection from "@/app/shared-components/question-footer-section";
import UnSavedChangesWarningMessage from "@/app/shared-components/un-saved-changes-warning-message/un-saved-changes-warning-message";
import withQuestionData from "@/app/shared-components/wrappers/with-question-data";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { APPLICATION } from "@/constants";

import DescriptionSection from "./component/description-section/description-section.layout";
import FormSectionLayout from "./component/form-section/form-section.layout";
import {
  useQuestionLayoutContext,
  QuestionLayoutProvider,
} from "./provider/question-layout-provider";
import { QuestionLayoutType } from "./types/question-layout-types";

const Footer = dynamic(
  () => import("@/components/organisms/footer/footer-section"),
  {
    ssr: false,
  }
);

function QuestionLayoutContent({
  formSection,
  descriptionContent,
  footer,
  isShowDynamicFooter = false,
  progressBarPercent = 0,
  backToRenewalSection = {
    isOpenUnsavedChangesModal: false,
    backToRenewalText: "",
    setIsOpenUnsavedChangesModal: () => {},
    onContinueClick: () => {},
  },
  isBackGroundImage,
}: Readonly<Omit<QuestionLayoutType, "scrollRefs">>) {
  const {
    formAnimationRef,
    footerAnimationRef,
    descriptionContentSpringRef,
    descriptionRef,
    editPage,
    footerRef,
    isLoading,
    isCancelEditModalOpen,
    dynamicFooter,
    setIsCancelEditModalOpen,
    handleCancelEditModalConfirmBtnClick,
  } = useQuestionLayoutContext();
  const {
    isOpenUnsavedChangesModal,
    backToRenewalText,
    onContinueClick,
    setIsOpenUnsavedChangesModal,
  } = backToRenewalSection;

  const { wizard, current } = useWizard();
  const { productSpecialFeatures } = wizard;
  const showNewButtonsLayout = current.uiElementControls?.showNewButtonsLayout;

  useUpdateProgressBar(progressBarPercent);

  return (
    <div
      data-testid="questionPage-test-id"
      className="bg-surface-neutral-option-2"
    >
      {productSpecialFeatures?.showCountryFooter && <CountryChangeModal />}
      <UnSavedChangesWarningMessage
        isOpen={isOpenUnsavedChangesModal}
        onContinueClick={onContinueClick}
        setIsOpen={setIsOpenUnsavedChangesModal}
      />
      <CancelEditModalContent
        isCancelEditOpen={isCancelEditModalOpen}
        handleConfirmBtnClick={() => handleCancelEditModalConfirmBtnClick()}
        handleCancelBtnClick={() => {
          setIsCancelEditModalOpen(false);
        }}
        setIsCancelEditOpen={(isOpen: boolean) => {
          setIsCancelEditModalOpen(isOpen);
        }}
      />
      <div className="max-w-[1200px] mx-auto my-0 w-[100%] relative min-h-[calc(100vh-12px)] lg:pb-0 pb-spacing-5xl ">
        <main
          id="question-page"
          aria-label="question-page"
          className=" lg:py-spacing-xxl md:py-spacing-xl py-spacing-l"
        >
          <div
            className=" grid md:grid-cols-12 grid-cols-6 md:gap-spacing-l gap-spacing-m xl:mx-auto lg:mx-[72px] 
                        md:mx-[48px] mx-[24px] relative"
          >
            <div className="lg:col-span-7 col-span-12 bg-surface-neutral-option-1 rounded-radius-xxl">
              <FormSectionLayout
                content={formSection}
                footer={footer}
                formAnimationRef={formAnimationRef}
                footerAnimationRef={footerAnimationRef}
                footerRef={footerRef}
                backToRenewalsText={backToRenewalText}
                setIsOpenUnsavedChangesModal={setIsOpenUnsavedChangesModal}
                editPage={editPage}
                isLoading={isLoading}
                isCancelEditModalOpen={isCancelEditModalOpen}
                setIsCancelEditModalOpen={setIsCancelEditModalOpen}
              />
            </div>

            <div className=" lg:col-span-5 col-span-12">
              <DescriptionSection
                content={descriptionContent}
                descriptionContentSpringRef={descriptionContentSpringRef}
                descriptionRef={descriptionRef}
                isBackGroundImage={isBackGroundImage}
              />
            </div>
          </div>
          <QuestionFooterSection />
          {productSpecialFeatures?.useNewHelpOverlay && <CookiePolicyButton />}
        </main>
        {dynamicFooter && APPLICATION === "quote" && isShowDynamicFooter && (
          <footer data-testid="footer">
            <Footer />
          </footer>
        )}
      </div>
    </div>
  );
}

function QuestionLayout(props: Readonly<QuestionLayoutType>) {
  const { scrollRefs = [], children, ...restProps } = props;

  return (
    <QuestionLayoutProvider scrollRefs={scrollRefs}>
      {children}
      <QuestionLayoutContent
        formSection={restProps.formSection}
        descriptionContent={restProps.descriptionContent}
        footer={restProps.footer}
        isShowDynamicFooter={restProps.isShowDynamicFooter}
        progressBarPercent={restProps.progressBarPercent}
        backToRenewalSection={restProps.backToRenewalSection}
        isBackGroundImage={restProps.isBackGroundImage}
      />
    </QuestionLayoutProvider>
  );
}

export default withQuestionData(QuestionLayout);
